import React, { useState, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DashboardProps } from 'pages/Dashboard/Dashboard.props';
import { useAppSelector } from 'store/hooks';
import {
  accountSelector,
  activeGroupNameSelector,
  dashboardSettingsSelector,
} from 'store/account/account.selector';
import { hasFetchedDashboardSettingsSelector } from 'store/account/account.selector';
import {
  progInFullScreenSelector,
  userProgramSelector,
  selectNotificationType,
} from 'store/app/app.selector';
import { useAppDispatch } from 'store/hooks';
import {
  ADD_COLUMNS,
  UPDATE_PROGRAMS_SETTINGS,
  UPDATE_PROGRAMS,
} from 'store/programs/programs.actions';
import { SET_NOTIFICATION_TYPE } from 'store/app/app.actions';
import { jwtSelector } from 'store/auth/auth.selector';
import {
  hasInitializedColumnsSelector,
  programColumnsSelector,
} from 'store/programs/programs.selector';
import DashboardInitial from 'pages/Dashboard/Dashboard.initial';
import DashboardView from 'pages/Dashboard/Dashboard.view';
import { useContainerDimensions } from 'hooks/useContainerDimensions';

declare global {
  interface Window {
    activeIncidents?: number[];
    hideAssignedIncModal?: boolean;
  }
}

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const account = useAppSelector(accountSelector);
  const username = account?.username || '';
  const groupName = useAppSelector(activeGroupNameSelector) || '';
  const dashboardSettings = useAppSelector(dashboardSettingsSelector);
  const hasFetchedDashboardSettings = useAppSelector(hasFetchedDashboardSettingsSelector);
  const hasInitializedColumns = useAppSelector(hasInitializedColumnsSelector);
  const notificationType = useAppSelector(selectNotificationType);
  const progInFullScreen = useAppSelector(progInFullScreenSelector);
  const token = useAppSelector(jwtSelector);
  const programsList = useAppSelector(userProgramSelector);
  const columns = useAppSelector(programColumnsSelector);

  // Memoize JSON parsing to avoid repeated parsing on every render
  const settings = useMemo(() => {
    if (!dashboardSettings?.pvSettingValue) return null;
    try {
      return JSON.parse(dashboardSettings.pvSettingValue);
    } catch {
      return null;
    }
  }, [dashboardSettings?.pvSettingValue]);

  const onBoardingSettings = settings?.onBoarding || null;
  const programsHistory = settings?.programsHistory;
  const isInitialOnBoarding = !onBoardingSettings || !onBoardingSettings.isInitialized;

  const [openIncident, setOpenIncident] = useState<boolean>(false);
  const [openPositions, setOpenPositions] = useState<boolean>(false);
  const [openControlPanel, setOpenControlPanel] = useState(false);
  const [openOnBoarding, setOpenOnBoarding] = useState<boolean>(false);
  const [openContactSupport, setOpenContactSupport] = useState<boolean>(false);
  const [userToggled, setUserToggled] = useState<boolean>(false);
  const [openAssignedIncidents, setOpenAssignedIncidents] = useState<boolean>(false);
  const [showMainView, setShowMainView] = useState(false);
  const { containerRef, dimensions } = useContainerDimensions(!showMainView);

  const memoizedProgramsList = useMemo(() => programsList || [], [programsList]);
  const columnCount = useMemo(() => {
    return Object.keys(columns).length;
  }, [columns]);

  useEffect(() => {
    if (!settings || hasInitializedColumns) return;

    if (settings) {
      dispatch(UPDATE_PROGRAMS_SETTINGS(settings.selectedPrograms));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  useEffect(() => {
    if (!memoizedProgramsList) return;
    //special case for DOT Video Wall id 45
    const selectedProgramIds =
      memoizedProgramsList
        .filter((program) => program.isActive && Number(program.id) !== 45)
        .map((program) => program.id) || [];

    dispatch(UPDATE_PROGRAMS(selectedProgramIds));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedProgramsList]);

  useEffect(() => {
    if (!hasFetchedDashboardSettings || userToggled) return;
    const hideAssignedIncModal = window.hideAssignedIncModal !== undefined;
    const showOnboarding = (): boolean => {
      if (window.dbSettingDone !== undefined && window.dbSettingDone) return false;
      if (!onBoardingSettings) return true;
      const { isInitialized, hasUpdates, hasCheckedHide } = onBoardingSettings;
      return hasUpdates || !isInitialized || !hasCheckedHide;
    };
    setOpenOnBoarding(showOnboarding());
    if (hideAssignedIncModal) setShowMainView(true);
    else setOpenAssignedIncidents(!showOnboarding());
  }, [hasFetchedDashboardSettings, dashboardSettings, onBoardingSettings, userToggled]);

  useEffect(() => {
    const handleOpenOnboarding = () => {
      setUserToggled(true);
      setOpenOnBoarding(true);
    };
    window.addEventListener('open-onboarding', handleOpenOnboarding);
    return () => window.removeEventListener('open-onboarding', handleOpenOnboarding);
  }, []);

  useEffect(() => {
    const handleOpenIncident = () => {
      setOpenIncident(true);
    };
    window.addEventListener('open-incident', handleOpenIncident);
    return () => window.removeEventListener('open-incident', handleOpenIncident);
  }, []);

  useEffect(() => {
    const handleOpenPositions = () => {
      setOpenPositions(true);
    };
    window.addEventListener('open-positions', handleOpenPositions);
    return () => window.removeEventListener('open-positions', handleOpenPositions);
  }, []);

  useEffect(() => {
    const handleOpenControlPanel = () => {
      setOpenControlPanel(true);
    };
    window.addEventListener('open-control-panel', handleOpenControlPanel);
    return () => window.removeEventListener('open-control-panel', handleOpenControlPanel);
  }, []);

  useEffect(() => {
    const handleOpenContactSupport = () => {
      setOpenContactSupport(true);
    };
    window.addEventListener('open-contact-support', handleOpenContactSupport);
    return () => window.removeEventListener('open-contact-support', handleOpenContactSupport);
  }, []);

  //add ev listener for setOpenContactSupport(false)
  useEffect(() => {
    const handleCloseContactSupport = () => {
      setOpenContactSupport(false);
    };
    window.addEventListener('close-contact-support', handleCloseContactSupport);
    return () => window.removeEventListener('close-contact-support', handleCloseContactSupport);
  }, []);

  const toggleDrawer = () => {
    window.dispatchEvent(new CustomEvent('toggle-sidebar'));
  };

  const toggleNotificationType = (value: 'toast' | 'marquee') => {
    dispatch(SET_NOTIFICATION_TYPE(value));
  };

  const onCloseOnBoarding = () => {
    setOpenOnBoarding(false);
    setUserToggled(true);
    if (showMainView) return;
    setOpenAssignedIncidents(() => true);
  };

  const onCloseAssignedIncidents = () => {
    setOpenAssignedIncidents(false);
    setShowMainView(true);
    window.dbSettingDone = true;
  };

  const onOpenIncidentsTable = () => {
    setUserToggled(true);
    setOpenAssignedIncidents(false);
    if (showMainView) {
      setOpenIncident(true);
      return;
    }
    setShowMainView(true);
    window.dbSettingDone = true;
    setTimeout(() => {
      setOpenIncident(true);
    }, 1000);
  };

  const addColumn = () => {
    const newColumnId = `column${uuidv4()}`;
    const payload = { [newColumnId]: [`widget`] };
    dispatch(ADD_COLUMNS(payload));
  };

  const combineProps: DashboardProps = {
    addColumn,
    columnCount,
    isInitialOnBoarding,
    openIncident,
    openPositions,
    openControlPanel,
    openContactSupport,
    openOnBoarding,
    onCloseContactSupport: () => setOpenContactSupport(false),
    onCloseIncident: () => setOpenIncident(false),
    onClosePositions: () => setOpenPositions(false),
    onCloseControlPanel: () => setOpenControlPanel(false),
    onCloseOnBoarding,
    toggleDrawer,
    onBoardingSettings,
    programsList: memoizedProgramsList,
    programsHistory,
    notificationType,
    toggleNotificationType,
    progInFullScreen,
    username,
    groupName,
  };

  if (showMainView) {
    return <DashboardView {...combineProps} />;
  }

  return (
    <div className={'bg-white-50 flex h-full w-full flex-col'} ref={containerRef}>
      <DashboardInitial
        dimensions={dimensions}
        groupName={groupName}
        isInitialOnBoarding={isInitialOnBoarding}
        onBoardingSettings={onBoardingSettings}
        onCloseAssignedIncidents={onCloseAssignedIncidents}
        onCloseOnBoarding={onCloseOnBoarding}
        onOpenIncidentsTable={onOpenIncidentsTable}
        openAssignedIncidents={openAssignedIncidents}
        openOnBoarding={openOnBoarding}
        programsHistory={programsHistory}
        programsList={memoizedProgramsList}
        token={token}
        username={username}
      />
    </div>
  );
};

export default Dashboard;
